/**
 * /earthworks/daily-logs/:id — one Earthworks daily log, read in the office
 * (GET / DELETE /earthworks/daily-logs/:id, fieldiq-api#1284). A full page.
 *
 * Shows the crew and unit hours (the hour-meter end on OWNED units only), the
 * loads hauled off and imported, the dump tickets with their photo, the
 * before / after photos, the warnings that were showing and who carried on
 * past each (and when), and the totals THE API returns — nothing is summed on
 * this side. An absent figure is `—`, never 0: a log with no tipping fee shows
 * `—` for the total, not $0.00.
 *
 * The log is written on the phone (/field/ew/jobs/:id/log/:workDate); the
 * office corrects it in the same form at /earthworks/jobs/:id/log/:workDate
 * (Edit, dispatch roles). Delete is dispatch roles, on an open job only.
 */
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CalendarDays, Clock, Pencil, Trash2, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useDeleteEwDailyLog, useEwDailyLog } from '../../hooks/useEwField';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canDispatchEw, ewDay, ewRefusal, fmtMoney, fmtNumber, withUnit } from '../../lib/earthworks';
import type { EwDailyLog } from '../../types/earthworks';
import { DH_BAND_SECONDARY } from '../downhole/DownholeBits';
import { useBackToList } from '../downhole/recordPage';
import {
  isNotFound,
  EW_EYEBROW,
  EW_PRIMARY,
  EW_SECONDARY,
  EwDialog,
  EwEmpty,
  EwFormError,
  EwLoading,
  EwPanel,
  EwRegister,
  EwSpec,
  type EwColumn,
} from './EwBits';
import { EwWarningCards, fromOfficeWarning } from './EwWarnings';

type CrewLine = EwDailyLog['crew'][number];
type UnitLine = EwDailyLog['equipment'][number];
type LoadLine = EwDailyLog['loads'][number];
type DumpLine = EwDailyLog['dumpTickets'][number];
const SETTLED = { isLoading: false };

export function EwLogReadout({ log, currency, timeZone }: { log: EwDailyLog; currency: string | null; timeZone: string | null }) {
  const crewColumns: EwColumn<CrewLine>[] = [
    { key: 'name', header: 'Name', render: (c) => <span className="font-extrabold text-white">{c.userName || '—'}</span> },
    { key: 'hours', header: 'Hours', align: 'right', render: (c) => fmtNumber(c.hours) },
    { key: 'standby', header: 'Standby', align: 'right', render: (c) => fmtNumber(c.standbyHours) },
    { key: 'notes', header: 'Notes', render: (c) => c.notes || '—' },
  ];
  const unitColumns: EwColumn<UnitLine>[] = [
    {
      key: 'unit',
      header: 'Unit',
      render: (e) => (
        <>
          <p className="font-extrabold text-white">{e.unitName || '—'}</p>
          <p className="mt-0.5 text-xs text-fieldiq-muted">{e.kind === 'rent_in' ? `Rent-in · ${e.vendorName ?? '—'}` : `Owned · ${e.unitNumber ?? '—'}`}</p>
        </>
      ),
    },
    { key: 'hours', header: 'Hours', align: 'right', render: (e) => fmtNumber(e.hours) },
    { key: 'standby', header: 'Standby', align: 'right', render: (e) => fmtNumber(e.standbyHours) },
    { key: 'meter', header: 'Hour meter end', align: 'right', render: (e) => (e.kind === 'owned' ? fmtNumber(e.hourMeterEnd) : '—') },
    { key: 'check', header: 'Pre-use check', render: (e) => (e.check ? `${(e.check.status ?? '—').replace(/_/g, ' ')} · ${e.check.checkedBy ?? '—'}` : '—') },
    { key: 'notes', header: 'Notes', render: (e) => e.notes || '—' },
  ];
  const loadColumns: EwColumn<LoadLine>[] = [
    { key: 'direction', header: 'Direction', render: (l) => <span className="font-extrabold text-white">{l.directionLabel || l.direction}</span> },
    { key: 'material', header: 'Material', render: (l) => l.materialLabel || l.material || '—' },
    { key: 'count', header: 'Loads', align: 'right', render: (l) => fmtNumber(l.loadCount) },
    { key: 'quantity', header: 'Quantity', align: 'right', render: (l) => withUnit(l.quantity, l.quantityUnit) },
    { key: 'truck', header: 'Truck / hauler', render: (l) => [l.truckName, l.haulerName].filter(Boolean).join(' · ') || '—' },
    { key: 'notes', header: 'Notes', render: (l) => l.notes || '—' },
  ];
  const dumpColumns: EwColumn<DumpLine>[] = [
    { key: 'ticket', header: 'Ticket #', render: (d) => <span className="font-mono font-extrabold text-white">{d.ticketNumber || '—'}</span> },
    { key: 'facility', header: 'Facility', render: (d) => d.facilityName || '—' },
    { key: 'material', header: 'Material', render: (d) => d.materialLabel || d.material || '—' },
    { key: 'weight', header: 'Net weight', align: 'right', render: (d) => withUnit(d.netWeight, d.weightUnit) },
    { key: 'fee', header: 'Tipping fee', align: 'right', render: (d) => <span data-testid="ew-log-dump-fee">{fmtMoney(d.tippingFee, currency)}</span> },
    {
      key: 'photo',
      header: 'Photo',
      render: (d) =>
        d.photo?.url ? (
          <a href={d.photo.url} target="_blank" rel="noreferrer" aria-label={`Ticket ${d.ticketNumber} photo`}>
            <img src={d.photo.url} alt={`Ticket ${d.ticketNumber}`} className="h-12 w-12 rounded-fi-control object-cover" />
          </a>
        ) : (
          '—'
        ),
    },
  ];
  const t = log.totals;
  return (
    <>
      <EwPanel eyebrow="Daily log" title="The day" testId="ew-log-header">
        <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
          <EwSpec label="Work" value={log.workKinds.length ? log.workKinds.map((k) => k.label).join(', ') : '—'} />
          <EwSpec label="Weather" value={log.weather || '—'} />
          <EwSpec label="Entered by" value={log.createdByName || '—'} />
          <EwSpec label="Last changed by" value={log.updatedByName || '—'} />
        </dl>
        <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text"><span className="font-bold">Work done: </span>{log.narrative || '—'}</p>
        <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text"><span className="font-bold">Safety: </span>{log.safetyNotes || '—'}</p>
      </EwPanel>

      <EwPanel eyebrow="Totals" title="Totals" testId="ew-log-totals">
        <dl className="grid gap-4 px-4 py-4 sm:grid-cols-3 lg:grid-cols-5">
          <EwSpec label="Crew hours" value={fmtNumber(t.crewHours)} mono />
          <EwSpec label="Crew standby" value={fmtNumber(t.crewStandbyHours)} mono />
          <EwSpec label="Owned unit hours" value={fmtNumber(t.ownedEquipmentHours)} mono />
          <EwSpec label="Rent-in hours" value={fmtNumber(t.rentInHours)} mono />
          <EwSpec label="Loads hauled off" value={fmtNumber(t.loadsHauledOff)} mono />
          <EwSpec label="Loads imported" value={fmtNumber(t.loadsImported)} mono />
          <EwSpec label="Dump tickets" value={fmtNumber(t.dumpTickets)} mono />
          <EwSpec label="Tipping fees" value={<span data-testid="ew-log-total-fees">{fmtMoney(t.tippingFees, currency)}</span>} mono />
          <EwSpec label="Photos" value={fmtNumber(t.photos)} mono />
        </dl>
      </EwPanel>

      <EwPanel eyebrow="Warnings" title="Warnings and who carried on" testId="ew-log-warnings-read">
        {log.warnings.length === 0 ? (
          <EwEmpty>No warning was showing for this day.</EwEmpty>
        ) : (
          <div className="px-4 py-4">
            <EwWarningCards warnings={log.warnings.map(fromOfficeWarning)} timeZone={timeZone} testId="ew-log-warning-list" />
          </div>
        )}
      </EwPanel>

      <EwPanel eyebrow="Crew" title="Crew hours" testId="ew-log-crew-read">
        <EwRegister query={SETTLED} rows={log.crew} columns={crewColumns} rail={() => 'border-l-fi-steel'} subject="crew hours" testId="ew-log-crew-row" minWidth="min-w-[640px]"
          empty={<EwEmpty>No crew hours on this log.</EwEmpty>}
          card={(c) => <p className="text-sm text-fieldiq-text"><span className="font-extrabold text-white">{c.userName || '—'}</span> · {fmtNumber(c.hours)} h · standby {fmtNumber(c.standbyHours)} h</p>} />
      </EwPanel>

      <EwPanel eyebrow="Equipment" title="Unit hours" testId="ew-log-units-read">
        <EwRegister query={SETTLED} rows={log.equipment} columns={unitColumns} rail={(e) => (e.kind === 'rent_in' ? 'border-l-fi-blue' : 'border-l-fi-steel')} subject="unit hours" testId="ew-log-unit-row" minWidth="min-w-[900px]"
          empty={<EwEmpty>No unit hours on this log.</EwEmpty>}
          card={(e) => (
            <p className="text-sm text-fieldiq-text">
              <span className="font-extrabold text-white">{e.unitName || '—'}</span> · {fmtNumber(e.hours)} h
              {e.kind === 'owned' ? ` · meter ${fmtNumber(e.hourMeterEnd)}` : ' · rent-in'}
            </p>
          )} />
      </EwPanel>

      <EwPanel eyebrow="Trucking" title="Loads" testId="ew-log-loads-read">
        <EwRegister query={SETTLED} rows={log.loads} columns={loadColumns} rail={(l) => (l.direction === 'import' ? 'border-l-fi-blue' : 'border-l-fi-amber')} subject="loads" testId="ew-log-load-row" minWidth="min-w-[820px]"
          empty={<EwEmpty>No loads on this log.</EwEmpty>}
          card={(l) => (
            <p className="text-sm text-fieldiq-text">
              <span className="font-extrabold text-white">{l.directionLabel || l.direction}</span> · {l.materialLabel || l.material} · {fmtNumber(l.loadCount)} loads · {withUnit(l.quantity, l.quantityUnit)}
            </p>
          )} />
      </EwPanel>

      <EwPanel eyebrow="Disposal" title="Dump tickets" testId="ew-log-dumps-read">
        <EwRegister query={SETTLED} rows={log.dumpTickets} columns={dumpColumns} rail={() => 'border-l-fi-steel'} subject="dump tickets" testId="ew-log-dump-row" minWidth="min-w-[820px]"
          empty={<EwEmpty>No dump tickets on this log.</EwEmpty>}
          card={(d) => (
            <p className="text-sm text-fieldiq-text">
              <span className="font-mono font-extrabold text-white">{d.ticketNumber}</span> · {d.facilityName} · {withUnit(d.netWeight, d.weightUnit)} · {fmtMoney(d.tippingFee, currency)}
            </p>
          )} />
      </EwPanel>

      <EwPanel eyebrow="Photos" title="Before and after" testId="ew-log-photos-read">
        {log.photos.length === 0 ? (
          <EwEmpty>No photos on this log.</EwEmpty>
        ) : (
          <ul className="grid gap-3 px-4 py-4 sm:grid-cols-3 lg:grid-cols-6">
            {log.photos.map((p) => (
              <li key={p.id} className="space-y-1">
                {p.file?.url ? (
                  <a href={p.file.url} target="_blank" rel="noreferrer">
                    <img src={p.file.url} alt={`${p.stageLabel ?? p.stage} stage`} className="aspect-square w-full rounded-fi-control object-cover" />
                  </a>
                ) : (
                  <div className="flex aspect-square w-full items-center justify-center rounded-fi-control border border-fieldiq-border text-xs text-fieldiq-muted">—</div>
                )}
                <p className="text-xs font-bold text-white">{p.stageLabel || p.stage}</p>
                {p.caption && <p className="text-xs text-fieldiq-text">{p.caption}</p>}
              </li>
            ))}
          </ul>
        )}
      </EwPanel>
    </>
  );
}

export default function EwDailyLogScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const query = useEwDailyLog(id);
  const del = useDeleteEwDailyLog();
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const log = query.data?.data ?? null;
  const meta = query.data?.meta ?? null;
  const back = useBackToList(log ? `/earthworks/jobs/${log.jobId}?tab=logs` : '/earthworks/jobs');
  const failure = queryLoadFailure(query);
  const notFound = isNotFound(queryFailureError(query));
  const canDelete = canDispatchEw(user?.role);

  return (
    <div data-testid="ew-daily-log-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={log?.jobNumber ? `${EW_EYEBROW} · ${log.jobNumber}` : EW_EYEBROW}
        title={`Daily log · ${ewDay(log?.workDate)}`}
        onBack={back}
        backLabel="Back"
        actions={
          log && canDelete ? (
            <div className="flex items-center gap-2">
              <button type="button" className={DH_BAND_SECONDARY} onClick={() => navigate(`/earthworks/jobs/${log.jobId}/log/${log.workDate}`)}>
                <Pencil className="size-4" aria-hidden="true" />
                Edit
              </button>
              <button type="button" className={DH_BAND_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                <Trash2 className="size-4" aria-hidden="true" />
                Delete
              </button>
            </div>
          ) : undefined
        }
        stats={[
          { icon: <CalendarDays className="size-4" aria-hidden="true" />, label: 'Work date', tone: 'neutral', loading: query.isLoading, value: ewDay(log?.workDate) },
          { icon: <Clock className="size-4" aria-hidden="true" />, label: 'Crew hours', tone: 'neutral', loading: query.isLoading, value: fmtNumber(log?.totals.crewHours) },
          { icon: <Truck className="size-4" aria-hidden="true" />, label: 'Loads off / in', tone: 'neutral', loading: query.isLoading, value: log ? `${fmtNumber(log.totals.loadsHauledOff)} / ${fmtNumber(log.totals.loadsImported)}` : '—' },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <EwLoading label="Loading daily log" />
          ) : notFound || (!failure && !log) ? (
            <EwPanel eyebrow="Daily log" title="Daily log">
              <EwEmpty>This daily log was not found. It may have been deleted.</EwEmpty>
            </EwPanel>
          ) : failure || !log ? (
            <EwPanel eyebrow="Daily log" title="Daily log">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this daily log" />
            </EwPanel>
          ) : (
            <EwLogReadout log={log} currency={meta?.currency ?? null} timeZone={meta?.timeZone ?? null} />
          )}
        </div>
      </section>

      {log && canDelete && (
        <EwDialog
          open={deleting}
          title="Delete daily log"
          onClose={() => setDeleting(false)}
          footer={
            <>
              <button type="button" className={EW_SECONDARY} onClick={() => setDeleting(false)} disabled={del.isPending}>
                Keep log
              </button>
              <button
                type="button"
                className={EW_PRIMARY}
                disabled={del.isPending}
                onClick={() =>
                  del.mutate(log.id, {
                    onSuccess: () => {
                      toast.success('Daily log deleted');
                      navigate(`/earthworks/jobs/${log.jobId}?tab=logs`, { replace: true });
                    },
                    onError: (err) => setDeleteError(ewRefusal(err, 'The daily log could not be deleted.')),
                  })
                }
              >
                {del.isPending ? 'Deleting…' : 'Delete log'}
              </button>
            </>
          }
        >
          <EwFormError message={deleteError} />
          <p className="text-sm text-fieldiq-text">Removes the {ewDay(log.workDate)} log and its hours, loads, tickets and photos from the job.</p>
        </EwDialog>
      )}
    </div>
  );
}
